import camelcaseKeys from 'camelcase-keys'
import { create } from 'zustand'

import { executeGet, executePost } from '@/services/lukita'
import {
  InvitationExpiredError,
  InvitationMismatchError,
  InvitationNotFoundError,
} from '../errors'
import type { InvitationDetails, InvitationDetailsDTO } from './types'

type Kind =
  | 'loading'
  | 'loaded'
  | 'not_found'
  | 'expired'
  | 'prompt_login'
  | 'accepting'
  | 'accepted'
  | 'mismatch'
  | 'error'

interface InvitationStoreActions {
  reset: () => void
  setDetails: (details: InvitationDetails | InvitationDetailsDTO | null) => void
  setKind: (kind: Kind, error?: string | null) => void
}

interface InvitationStoreMutations {
  acceptInvitation: (token: string) => Promise<void>
}

interface InvitationStoreLoaders {
  fetchInvitation: (token: string) => Promise<void>
}

interface InvitationStore {
  details: InvitationDetails | null
  error: string | null
  kind: Kind
  actions: InvitationStoreActions
  mutations: InvitationStoreMutations
  loaders: InvitationStoreLoaders
}

export const useInvitationStore = create<InvitationStore>((set, get, store) => {
  return {
    details: null,
    error: null,
    kind: 'loading',

    actions: {
      reset: () => set(store.getInitialState()),
      setKind: (kind, error = null) => set({ kind, error }),
      setDetails: (value) => {
        const details = value ? camelcaseKeys(value) : null
        let kind: Kind = 'loaded'

        if (details?.hasExpired) {
          kind = 'expired'
        }
        set({ details, kind })
      },
    },

    mutations: {
      acceptInvitation: async (token) => {
        const { setKind } = get().actions
        try {
          setKind('accepting')
          await executePost(`/invitations/${token}/accept`)
          setKind('accepted')
        } catch (err: unknown) {
          const message =
            err instanceof Error ? err.message : 'Failed to accept invitation'
          setKind('error', message)
        }
      },
    },

    loaders: {
      fetchInvitation: async (token) => {
        const { setDetails, setKind } = get().actions
        try {
          const response = await executeGet(`/invitations/${token}`)
          const details = await response.json()
          setDetails(details)
        } catch (error) {
          if (error instanceof InvitationExpiredError) {
            setKind('expired', error.message)
            return
          }
          if (error instanceof InvitationNotFoundError) {
            setKind('not_found', error.message)
            return
          }
          setKind('not_found')
        }
      },
    },
  }
})

export const useInvitationActions = () =>
  useInvitationStore((state) => state.actions)
export const useInvitationMutations = () =>
  useInvitationStore((state) => state.mutations)
export const useInvitationLoaders = () =>
  useInvitationStore((state) => state.loaders)
export const useInvitation = () => {
  const kind = useInvitationStore((state) => state.kind)
  const error = useInvitationStore((state) => state.error)
  const details = useInvitationStore((state) => state.details)

  return { kind, error, details }
}
